import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { PulseSurvey, PulseSurveyViewer } from "@pulselabs-ai/survey-handler";
import "./SurveyForm.scss";
import ThemeData from "./theme.json";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { PulseButton, PulseButtonTheme } from "@pulselabs-ai/shared-fe-ui";
import { CloseIcon, RefreshIcon } from "../icons";
import MobileFrame from "./mobile.svg";
import "@pulselabs-ai/survey-handler/dist/cjs/index.css";

interface SurveyFormProps {
  onSave: (json: SurveyJson) => void;
  onClose: () => void;
  isEditing: boolean;
  initialData?: SurveyJson;
}

export type SurveyJson = {
  title?: string;
  description?: string;
  pages?: Array<{
    name: string;
    elements: Array<{
      type: string;
      name: string;
      title: string;
      choices?: Array<{ value: string; text: string }>;
    }>;
  }>;
};

const sampleSurveyJson = {};

const FORM_TYPE = "Form";

const SurveyForm: React.FC<SurveyFormProps> = ({
  onSave,
  onClose,
  isEditing,
  initialData,
}) => {
  const jsonSurvey = initialData || sampleSurveyJson;
  const [editorSurvey, setEditorSurvey] = useState<SurveyJson>(jsonSurvey);
  const [previewSurvey, setPreviewSurvey] = useState<SurveyJson>(jsonSurvey);
  const [hasChanges, setHasChanges] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const showSurveyPreview = true;

  console.log("initialData", initialData);
  console.log("editorSurvey", editorSurvey);

  useEffect(() => {
    // Increase delay and add loading state
    setIsLoading(true);
    const timer = setTimeout(() => {
      if (containerRef.current) {
        setIsReady(true);
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (initialData) {
      setEditorSurvey(initialData);
      setPreviewSurvey(initialData);
    } else {
      setEditorSurvey(jsonSurvey);
      setPreviewSurvey(jsonSurvey);
    }
  }, [initialData]);

  const handleSurveyChange = (newSurvey: SurveyJson) => {
    console.log("handle", newSurvey);
    try {
      // if (!newSurvey || !newSurvey.pages) {
      //   console.warn("Invalid survey data received");
      //   return;
      // }
      setEditorSurvey(newSurvey);
      setHasChanges(true);
    } catch (error) {
      console.error("Failed to handle survey change:", error);
    }
  };

  const handleRefreshPreview = () => {
    setPreviewSurvey(editorSurvey);
    setHasChanges(false);
  };

  const renderSurveyEditor = () => {
    if (isLoading) {
      return <div>Loading survey editor...</div>;
    }

    try {
      return (
        isReady &&
        containerRef.current && (
          <div className="survey-editor">
            <PulseSurvey
              configType="flightRecorderConfig"
              config={{ themeData: ThemeData }}
              surveyJson={editorSurvey}
              onSave={handleSurveyChange}
              surveyType={FORM_TYPE}
            />
          </div>
        )
      );
    } catch (error) {
      console.error("Failed to render survey editor:", error);
      return <div>Error loading survey editor</div>;
    }
  };

  const onHandleSave = () => {
    console.log("Save clicked");
    onSave(editorSurvey);
  };

  return (
    <div className="survey-form">
      <div className="form-container" ref={containerRef}>
        <ErrorBoundary fallback={<div>Failed to load survey editor</div>}>
          <div className="form-builder">
            <div className="form-builder-title">
              {isEditing ? `Update ${FORM_TYPE}` : `Create ${FORM_TYPE}`}
            </div>
            {renderSurveyEditor()}
          </div>
        </ErrorBoundary>
        {showSurveyPreview && (
          <>
            <div className="survey-container">
              <IconButton onClick={onClose} className="close-button">
                <CloseIcon />
              </IconButton>

              <ErrorBoundary
                fallback={<div>Failed to load survey preview</div>}
              >
                <div className="survey-component">
                  {isReady && containerRef.current && (
                    <>
                      <img
                        src={MobileFrame}
                        alt="Mobile frame"
                        className="mobile-frame"
                      />
                      <div className="survey-preview">
                        <PulseSurveyViewer
                          surveyJson={JSON.stringify(previewSurvey)}
                        />
                      </div>
                      {hasChanges && (
                        <div className="preview-overlay">
                          <PulseButton
                            onClick={handleRefreshPreview}
                            className="refresh-button"
                            theme={PulseButtonTheme.SECONDARY}
                            startIcon={<RefreshIcon />}
                          >
                            Refresh Preview
                          </PulseButton>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ErrorBoundary>
            </div>
            <div className="button-panel">
              <PulseButton
                onClick={onHandleSave}
                className="save-button"
                fullWidth={false}
              >
                {isEditing ? `Update ${FORM_TYPE}` : `Create ${FORM_TYPE}`}
              </PulseButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyForm;
